import {
  createMutationStore,
  createQueryStore,
} from '@ferreri/minato-react-utils';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useQueryClient } from '@ferreri/minato-react-utils';
import { alert } from '@ferreri/prever-component-lib';
import { useBusinessUnitStoredId } from '../../business-units/business-unit.store';
import { useBenefitCategories } from '../../plan-benefit/plan-benefit.store';
import { Benefit } from '../../plan-benefit/plan-benefit.types';
import {
  usePlanDistributionIdRouteParam,
  usePlanDistributions,
} from '../../plan-distributions/plan-distribution.store';
import { getPlanVersions } from '../../plan/plan.client';
import {
  createPlanVersion,
  deleteContractDocument,
  deleteInsuranceDocument,
  getPlanVersion,
  getPlanVersionDocumentContract,
  getPlanVersionDocumentInsure,
  getPlanVersionManifest,
  listPlanVersionsByBusinessUnit,
  listPlanVersionsByPlanDistribution,
  putPlanVersionIsConfirmed,
  updatePlanVersion,
  uploadContractDoc,
  uploadInsuranceContractDoc,
} from './plan-version.client';
import { BenefitItem, PlanVersion } from './plan-version.type';
import { useFoundAlphaUser } from '@ferreri/minato-auth-react';

export const usePlanVersion = (id: string) => {
  return createQueryStore({
    queryKey: ['plan-version', id],
    queryFn: () => getPlanVersion(id),
    enabled: !!id,
  });
};

export const usePlanVersionManifest = (id: string) => {
  return createQueryStore({
    queryKey: ['plan-version-manifest', id],
    queryFn: () => getPlanVersionManifest(id),
    enabled: !!id,
  });
};

export const useCurrentPlanVersionManifest = () => {
  const planVersionId = usePlanVersionIdRouteParam();

  return usePlanVersionManifest(planVersionId);
};

const convertPlanBenefitsItems = (
  benefits: Benefit[],
  planVersion,
): BenefitItem[] => {
  const slug = planVersion?.plan_distribution?.plan?.slug;
  const isMulti = slug == `multi`;
  const isBemSeguro = slug == `bem-seguro`;
  const includedBenefitsInMulti = [
    'Seguro mobilidade emergencial',
    'Assistência residencial com eletro',
    'Assistência automóvel',
  ];

  return benefits?.map((benefit) => {
    const benefitItem: BenefitItem = {
      ...benefit,
      status: 'inactive',
    };
    const benefitRule = planVersion?.benefit_rules?.find(
      (rule) => rule.benefit.id === benefit.id,
    );
    const isOptional =
      benefitRule?.benefit_rule_inclusion?.type !=
      'BenefitRuleInclusionIncluded';
    const isIncluded =
      benefitRule?.benefit_rule_inclusion?.type ==
      'BenefitRuleInclusionIncluded';

    if (isMulti || isBemSeguro) {
      const isActive = includedBenefitsInMulti?.includes(benefit.name);
      if (isActive) return { ...benefitItem, status: 'active' };
    }

    if (!benefitRule) {
      return benefitItem;
    }

    if (isIncluded) {
      return { ...benefitItem, status: 'active' };
    }

    if (isOptional) {
      return { ...benefitItem, status: 'optional' };
    }

    return { ...benefitItem, status: 'active' };
  });
};

export const useBenefitItemsFromPlanVersion = (
  benefits: Benefit[],
  planVersion,
) =>
  useMemo(
    () => convertPlanBenefitsItems(benefits, planVersion),
    [benefits, planVersion],
  );

const getTransferLabel = (
  benefitItem: BenefitItem,
  rule: any,
): string | null => {
  if (benefitItem?.name !== 'Translado' || !rule) return null;

  const { coverage_region, coverage_radius } = rule;

  return coverage_region === 'state'
    ? `Translado livre no estado`
    : `Translado de até ${coverage_radius}km`;
};

const mapBenefitRulesById = (planVersion: PlanVersion) => {
  return planVersion?.benefit_rules?.reduce((acc, rule) => {
    const id = rule?.benefit?.id;
    if (id) acc[id] = rule;
    return acc;
  }, {} as Record<number, any>);
};

export const useBenefitsByCategoryGroup = (
  benefits: Benefit[],
  planVersion: PlanVersion,
) => {
  const { data: benefitCategories, isLoading: isLoadingCategories } =
    useBenefitCategories();
  const benefitItems = useBenefitItemsFromPlanVersion(benefits, planVersion);

  const categoryToGroupMap = useMemo(() => {
    return benefitCategories?.reduce((groupsByName, category) => {
      groupsByName[category?.id] = category?.benefit_category_group?.name;
      return groupsByName;
    }, {});
  }, [benefitCategories]);

  const benefitRulesMap = useMemo(() => {
    return mapBenefitRulesById(planVersion);
  }, [planVersion]);

  const benefitsWithCategoryGroup = useMemo(() => {
    if (!benefitItems) return {};

    return benefitItems.reduce((groupedBenefits, benefitItem) => {
      if (benefitItem?.is_comparison_hidden) return groupedBenefits;

      const groupName =
        categoryToGroupMap?.[benefitItem?.benefit_category_id] ?? 'Outros';

      if (!groupedBenefits[groupName]) groupedBenefits[groupName] = [];

      const rule = benefitRulesMap?.[benefitItem?.id];
      const transfer = getTransferLabel(benefitItem, rule);

      const formattedBenefit = {
        ...benefitItem,
        benefit_category_group: { name: groupName },
        ...(transfer && { transfer }),
      };

      groupedBenefits[groupName].push(formattedBenefit);
      return groupedBenefits;
    }, {} as Record<string, BenefitItem[]>);
  }, [benefitItems, categoryToGroupMap, planVersion]);

  return {
    benefitsWithCategoryGroup,
    isLoadingCategories,
  };
};

export const useGroupedBenefitItemsFromPlanVersion = (
  benefits: Benefit[],
  planVersion,
) => {
  const benefitItems = useBenefitItemsFromPlanVersion(benefits, planVersion);

  return useMemo(
    () =>
      benefitItems?.reduce(
        (acc, benefit) => {
          const key = benefit.status;

          return { ...acc, [key]: [...acc[key], benefit] };
        },
        {
          active: [],
          optional: [],
          inactive: [],
        },
      ),
    [benefitItems],
  );
};

export const usePlanVersions = (uf?: string) => {
  return createQueryStore({
    queryKey: ['planVersions', uf],
    queryFn: () => getPlanVersions(uf),
  });
};

export const useListPlanVersionsByBusinessUnit = (id: string) => {
  return createQueryStore({
    queryKey: ['plan-versions', id],
    queryFn: () => listPlanVersionsByBusinessUnit(id),
    enabled: !!id,
  });
};

export const useCreatePlanVersion = (config = null) => {
  return createMutationStore({
    mutationFn: createPlanVersion,
    ...config,
  });
};

export const useUpdatePlanVersion = (config = null) => {
  return createMutationStore({
    mutationFn: updatePlanVersion,
    ...config,
  });
};

export const useUploadPlanVersionContractDoc = (id: string, config = null) => {
  return createMutationStore({
    mutationFn: (data: File) => uploadContractDoc(id, data),
    retry: 0,
    ...config,
    onSuccess: () => {
      alert.successTimed(
        'Documento adicionado com sucesso',
        'A versão do plano foi atualizada com um novo documento.',
      );
    },
  });
};

export const usePlanVersionContractDoc = (id: string) => {
  return createQueryStore({
    queryKey: ['plan_version_document_contract', id],
    queryFn: () => getPlanVersionDocumentContract(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const usePlanVersionInsureDoc = (id: string) => {
  return createQueryStore({
    queryKey: ['plan_version_document_insure', id],
    queryFn: () => getPlanVersionDocumentInsure(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useUploadPlanVersionInsuranceContractDoc = (
  id: string,
  config = null,
) => {
  return createMutationStore({
    mutationFn: (data: File) => uploadInsuranceContractDoc(id, data),
    retry: 0,
    ...config,
    onSuccess: () => {
      alert.successTimed(
        'Documento adicionado com sucesso',
        'A versão do plano foi atualizada com um novo documento.',
      );
    },
  });
};

export const useListPlanVersionsByPlanDistribution = (
  id: string,
  status: string,
  enabled: boolean,
) => {
  return createQueryStore({
    queryKey: ['plan-versions', id, status],
    queryFn: () => listPlanVersionsByPlanDistribution(id, status),
    enabled,
  });
};

export const useCurrentListPlanVersions = () => {
  const businessUnitId = useBusinessUnitStoredId();
  const { data: foundAlphaUser } = useFoundAlphaUser();

  const listPlanVersions = useListPlanVersionsByBusinessUnit(businessUnitId);

  const filteredPlanVersions = listPlanVersions?.data?.filter((data) => {
    if (foundAlphaUser?.found) return true;

    return data?.plan_distribution?.plan?.slug !== 'alpha';
  });

  return { ...listPlanVersions, data: filteredPlanVersions };
};

export const useCurrentNeoPlanVersion = () => {
  const businessUnitId = useBusinessUnitStoredId();
  const { data, ...rest } = useListPlanVersionsByBusinessUnit(businessUnitId);

  const planVersion = useMemo(() => {
    const value = data?.find((planVersion) => {
      return planVersion?.plan_distribution?.plan?.slug == 'neo';
    });

    return value;
  }, [data]);

  return { data: planVersion, ...rest };
};

export const useCurrentDefaultPlanVersions = () => {
  const businessUnitId = useBusinessUnitStoredId();
  const { data, ...rest } = useListPlanVersionsByBusinessUnit(businessUnitId);

  const planVersion = useMemo(() => {
    const value = data?.filter((planVersion) => {
      return planVersion?.plan_distribution?.plan?.slug != 'neo';
    });

    return value;
  }, [data]);

  return { data: planVersion, ...rest };
};

export const usePlanVersionIdRouteParam = (): string => {
  const router = useRouter();

  return router.query?.plan_version_id as string;
};

const usePlanVersionsByCurrentPlanDistribution = (
  status: string,
  enabled: boolean,
) => {
  const planDistributionId = usePlanDistributionIdRouteParam();

  return useListPlanVersionsByPlanDistribution(
    planDistributionId,
    status,
    enabled,
  );
};

export const useDraftPlanVersionByCurrentPlanDistribution = (
  enabled = true,
) => {
  return usePlanVersionsByCurrentPlanDistribution('draft', enabled);
};

export const useActivePlanVersionByCurrentPlanDistribution = (
  enabled = true,
) => {
  const planVersionsQuery = usePlanVersionsByCurrentPlanDistribution(
    'active',
    enabled,
  );

  return { ...planVersionsQuery };
};

export const useHistoryPlanVersionByCurrentPlanDistribution = (
  enabled = true,
) => {
  return usePlanVersionsByCurrentPlanDistribution('history', enabled);
};

export const useUploadPlanVersionContractDocFromPlanVersionRouteParam = () => {
  const planVersionId = usePlanVersionIdRouteParam();

  return useUploadPlanVersionContractDoc(planVersionId);
};

export const useUploadPlanVersionInsuranceContractDocFromPlanVersionRouteParam =
  () => {
    const planVersionId = usePlanVersionIdRouteParam();

    return useUploadPlanVersionInsuranceContractDoc(planVersionId);
  };

export const useCurrentPlanVersion = () => {
  const planVersionId = usePlanVersionIdRouteParam();

  return usePlanVersion(planVersionId);
};

export const useActiveAndDraftPlanVersionsFromCurrentDistribution = () => {
  const draftQuery = useDraftPlanVersionByCurrentPlanDistribution();
  const activeQuery = useActivePlanVersionByCurrentPlanDistribution();

  const isLoading = draftQuery.isLoading || activeQuery.isLoading;
  const isError = draftQuery.isError || activeQuery.isError;
  const isFetching = draftQuery.isFetching || activeQuery.isFetching;

  const planVersions = { draft: draftQuery.data, active: activeQuery.data };

  return { data: planVersions, isLoading, isError, isFetching };
};

export const useAllPlanVersionsDistributions = () => {
  const planVersionQuery = usePlanVersions(null);

  return {
    ...planVersionQuery,
    data: planVersionQuery.data,
    isLoading: planVersionQuery.isLoading,
    isError: planVersionQuery.isError,
  };
};

export const useAllPlanVersionsDistributionsWithCurrentPlanVersion = (
  planId?: number,
) => {
  const { data: plansVersions, isLoading } = usePlanVersions(null);
  const { data: distributions, isLoading: isLoadingDistributions } =
    usePlanDistributions();

  const distributionsWithSlugVersion = useMemo(() => {
    if (!distributions || !plansVersions || !planId) return [];

    return distributions
      .filter((distribution) => distribution?.plan?.id === planId)
      .map((distribution) => {
        const matchingPlanVersion = plansVersions?.find(
          (planVersion) =>
            planVersion?.plan_distribution_id === distribution?.id,
        );

        return {
          ...distribution,
          plan: {
            ...distribution?.plan,
            slug_version: matchingPlanVersion?.slug,
          },
        };
      });
  }, [distributions, plansVersions, planId]);

  return {
    data: distributionsWithSlugVersion,
    isLoading: isLoading || isLoadingDistributions,
  };
};

export const useEditPlanVersionIsConfirmed = () => {
  return createMutationStore({
    mutationFn: putPlanVersionIsConfirmed,
  });
};

export const useDeleteContractDocument = () => {
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: deleteContractDocument,
    onSuccess: () => {
      queryClient.invalidateQueries(['plan_version_document_contract']);
      queryClient.invalidateQueries(['plan-versions']);
      alert.success(
        'Documento removido com sucesso',
        'A versão do plano não possui mais esse documento vinculado.',
      );
    },
  });
};

export const useDeleteInsuranceDocument = () => {
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: deleteInsuranceDocument,
    onSuccess: () => {
      queryClient.invalidateQueries(['plan_version_document_insurance']);
      queryClient.invalidateQueries(['plan-versions']);
      alert.success(
        'Documento removido com sucesso',
        'A versão do plano não possui mais esse documento vinculado.',
      );
    },
  });
};
